<template>
  <div class="login-page-container">
    <vpd-confirm-dialog name="banklist-confirm-dialog" headline="Átirányítás"
      subtitle="Bankod az alábbi adatok megadását kéri, hogy elindulhasson a kalkuláció. Ezután átirányítunk a bankod weboldalára, ahol a megszokott módon jelentkezz be."
      :dialog="dialog" :bankId="selectedBankId" :schema="schema" :loading="bankRedirectLoading" event="redirect-to-bank"
      v-on:redirect-to-bank="redirectToSelectedBankUrl" v-on:close-modal="cancelDialog">
    </vpd-confirm-dialog>

    <vpd-confirm-dialog name="banklist-confirm-no-data-dialog" headline="Átirányítás"
      subtitle="Most átirányítunk a bankod weboldalára, ahol a megszokott módon jelentkezz be." :dialog="noDataDialog"
      :bankId="null" :schema="null" event="redirect-to-bank" v-on:redirect-to-bank="redirectToSelectedBankUrl"
      v-on:close-modal="cancelNoDataDialog">
    </vpd-confirm-dialog>
    <vpd-error-dialog v-model="errorDialog" />

    <div class="login-page-info-container">
      <img :src="infoLogo" alt="Info" height="24px" />
      <span>Átirányítottunk a Nyíltbankolás Zrt oldalára. Kalkulátorunk a bankod által megosztott, elmúlt 3 havi
        számlatörténeted használja,
        kizárólag a Te engedélyeddel. Semmilyen pénzügyi műveletet nem végez.
      </span>
    </div>

    <!-- 🔹 Step 1: normal login panel -->
    <vpd-login-panel v-if="!requireOtp" @submit="submitLogin" @setSelectedBank="setSelectedBank" :config="config"
      :loading="loading" ref="loginPanel" />

    <!-- 🔹 Step 2: OTP verification -->
    <vpd-confirm-otp-panel v-else @submit="submitOtp" :loading="loading" ref="otpPanel" />
  </div>
</template>

<script>
import "./_Login.scss";
import VpdLoginPanel from "@/components/auth/vpd-login-panel/vpd-login-panel.vue";
import VpdConfirmOtpPanel from "@/components/auth/vpd-confirm-otp-panel/vpd-confirm-otp-panel.vue";
import vpdConfirmDialog from "@/components/vpd-confirm-dialog/vpd-confirm-dialog.vue";
import { signIn, confirmSignIn, fetchAuthSession, updateUserAttributes } from "@aws-amplify/auth";
import { signOut } from "../modules/auth";
import { AmplifyEventBus } from "aws-amplify-vue";
import infoLogo from "@/assets/info-icon.svg";
import { generatePassword } from "../modules/helpers";
import { signUp } from "../modules/auth";
import bankListService from "@/services/banklist-service";
import bankRequirementsService from "@/services/bank-requirements-service";
import vpdCustomDialog from "../components/vpd-custom-dialog/vpd-custom-dialog.vue";
import { AuthError } from '@aws-amplify/auth';
import vpdErrorDialog from "../components/vpd-error-dialog/vpd-error-dialog.vue";


const errorMessages = {
  UserNotConfirmedException: "A felhasználói fiók még nem lett aktiválva!",
  PasswordResetRequiredException: "A felhasználói jelszó beállítására van szükség!",
  NotAuthorizedException: "Érvénytelen felhasználónév vagy jelszó!",
  UserNotFoundException: "Hibás vagy nem létező felhasználó azonosító!",
  success: "Sikeres belépés!",
};

const lockoutErrorMessage = "Zárolt felhasználó.";

export default {
  name: "login",
  components: {
    VpdLoginPanel,
    VpdConfirmOtpPanel,
    vpdConfirmDialog,
    vpdCustomDialog,
    vpdErrorDialog
  },
  data: () => ({
    infoLogo,
    overlay: false,
    loading: false,
    bankRedirectLoading: false,
    dialog: false,
    noDataDialog: false,
    requireOtp: false,
    khConsentGiven: false,
    pendingUsername: null,
    selectedBank: null,
    selectedBankId: null,
    schema: null,
    path: null,
    errorDialog: false
  }),
  props: ["config"],
  methods: {
    async submitLogin(email, phone, selectedBank, dataStatus) {
      this.khConsentGiven = dataStatus;
      this.$gtag.event('calculation-start-clicked', {
        event_category: 'button',
        event_label: 'Calculation started'
      });
      const panel = this.$refs.loginPanel;
      this.loading = true;
      try {
        await signOut();
      }
      catch (e) { }
      let password = "";
      try {
        this.selectedBank = selectedBank;
        password = await generatePassword(phone);
        await signUp("khCalculatorUser", password, email, phone, this.khConsentGiven);
        const { isSignedIn, nextStep } = await signIn({ username: email, password });
        if (isSignedIn) {
          this.finishLogin();
          return;
        }

        if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
          this.requireOtp = true;
          this.pendingUsername = email;
          this.$nextTick(() => {
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: "auto"
            });
          });
          this.loading = false;
          return;
        }
      }
      catch (err) {
        if (err instanceof AuthError && err.name === 'UsernameExistsException') {
          const { isSignedIn, nextStep } = await signIn({ username: email, password });
          if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
            this.requireOtp = true;
            this.pendingUsername = email;
            this.$nextTick(() => {
              window.scrollTo({
                top: 0,
                left: 0,
                behavior: "auto"
              });
            });
            this.loading = false;
            return;
          }
        } else {
          console.error(err)
          this.errorDialog = true;
        }
      }
      finally {
        this.overlay = false;
        this.loading = false;
      }
    },

    async submitOtp(code) {
      this.$gtag.event('email-verifycation-clicked', {
        event_category: 'button',
        event_label: 'Email verified'
      });
      const panel = this.$refs.otpPanel;
      this.overlay = true;
      this.loading = true;

      try {
        // ✅ Confirm OTP with same session still in memory
        const { isSignedIn } = await confirmSignIn({ challengeResponse: code });

        if (isSignedIn) {
          await fetchAuthSession(); // refresh tokens
          this.finishLogin();
        } else {
          panel.showAlert("További azonosítás szükséges!", "warning");
        }
      } catch (error) {
        console.error("❌ OTP error:", error);
        this.errorDialog = true;
      } finally {
        this.overlay = false;
        this.loading = false;
      }
    },

    async finishLogin() {
      this.overlay = false;
      AmplifyEventBus.$emit("authState", "signedIn");

      if (this.khConsentGiven) {
        try {
          await updateUserAttributes({
            userAttributes: {
              'custom:khConsentGiven': '1',
            },
          });
        } catch (e) {
          this.errorDialog = true;
          console.error('Failed to update user attributes', e);
        }
      }

      this.setSelectedBank()
    },
    notAuthorizedExceptionHandler(panel) {
      panel.showAlert(errorMessages.NotAuthorizedException, "error");
    },
    userNotFoundExceptionHandler(panel) {
      panel.showAlert(errorMessages.UserNotFoundException, "error");
    },
    setSelectedBank() {
      if (!this.selectedBank) return;
      this.selectedBankId = this.selectedBank.id
      this.loading = true;
      bankRequirementsService.get(this.selectedBank.id).then((resp) => {
        if (!resp.items) {
          this.noDataDialog = true;
          this.loading = false;
          return;
        }
        this.schema = resp.items;
        this.loading = false;

        this.dialog = true;
      });
      this.path = this.selectedBank.auth.redirect;
    },
    cancelDialog() {
      this.dialog = false;
      this.path = null;
    },
    cancelNoDataDialog() {
      this.noDataDialog = false;
      this.path = null;
    },
    async redirectToSelectedBankUrl(formValues = null) {
      this.$gtag.event('redirection-to-bank-clicked', {
        event_category: 'button',
        event_label: 'Redirected to ASPSP'
      });
      const requestBodyObject = {};
      if (!formValues) {
        this.redirect(requestBodyObject)
      }
      const keys = Object.keys(formValues);

      keys.forEach(key => {
        requestBodyObject[key.toLocaleLowerCase()] = formValues[key]
      })

      this.dialog = false;

      this.redirect(requestBodyObject)
    },
    async redirect(requestBodyObject) {
      this.bankRedirectLoading = true;

      bankListService
        .select(this.path, requestBodyObject)
        .then((resp) => {
          if (resp && resp.url) {
            window.location.href = resp.url;
          }
        })
        .catch(() => {
          this.errorDialog = true;
        })
        .finally(() => {
          this.bankRedirectLoading = false;
        });
    }
  },
  mounted() {
    this.$gtag.event('calculator-login_page_loaded', {
      event_category: 'page',
      event_label: 'Calculator start page visitors'
    });
  },
};
</script>
